import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// import toast from 'react-hot-toast';

import { v4 as uuidv4 } from 'uuid';

import { useToastAction } from '../../../hooks/useToastAction';
import { useClassNames } from "../../../hooks/useClassNames";
import { useFeature } from '../../../hooks/useFeature';
import { useModalHelper } from "@metaforcelabs/metaforce-core";

import { TwTableContext } from '../../../contexts';

import { getInteractEnvironments, getEnvironmentGroup } from '../../../api/interact';
import { getInteractTemplateEnvironmentNoFiles } from "../../../api/interactTemplateEnvironment.js";
import { getInteractTemplateFile, getTemplateDocumentsEx } from "../../../api/interactTemplateFile.js";
import { getWorkflowAccess, getWorkflows, getWorkflowEnvironments, getSmartforms, getInteractTemplateFileProcessFromTemplateAndFileId, createInteractTemplateFileProcess, updateInteractTemplateFileProcess, deleteInteractTemplateFileProcess } from "../../../api/interactTemplateFileProcess.js";

import { EnvironmentGroupContainerCard } from "../Environments/environmentGroupContainerCard.js"
import GenericModal from '../../../components/Modals/genericModal';

import { pdfAttachmentPolicyOptions, EditLogicDocumentProcessesModal } from "./editLogicDocumentProcessesModal.js"

import { Button as CmpButton } from "../../../components/Button";
import { Label as CmpLabel } from "../../../components/Label";
import { Panel as CmpPanel } from "../../../components/Panel";
import MenuContextList from "../../../components/MenuContextList";
import { TwPage } from "../../../components/TailwindPage";
import { TwTable } from "../../../components/TailwindTable";
import { getDistributionLists } from '../../../api/distributionLists';
import { DistributionListsStatus } from '../../../components/DistributionListStatusBadge';
import { Badge, badgeTypes } from '../../../components/Badge';

export default function LogicFileProcessManager() {
  const { groupId, environmantId, fileId } = useParams();

  const refMenuContainerTo = useRef();

  const { hasFeature } = useFeature();
  const navigate = useNavigate();
  const { classNames } = useClassNames();
  const loadAction = useToastAction();
  const deleteAction = useToastAction();

  const editLogicDocumentProcessesModalHelper = useModalHelper();

  const [initialized, setInitialized] = useState(false)
  const [hasWorkflowLicense, setHasWorkflowLicense] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedProcesses, setSelectedProcesses] = useState([])

  const [environmentGroup, setEnvironmentGroup] = useState(null)
  const [environments, setEnvironments] = useState(null)
  const [environment, setEnvironment] = useState(null)
  const [logicFile, setLogicFile] = useState(null)
  const [workflows, setWorkflows] = useState([])
  const [smartforms, setSmartforms] = useState([])
  const [distributionLists, setDistributionLists] = useState([])

  const [paginationTotalCount, setPaginationTotalCount] = useState(0);
  const [paginationCurrentPage, setPaginationCurrentPage] = useState(0);
  const [paginationItemsPerPage, setPaginationItemsPerPage] = useState(50);

  const [pageProcesses, setPageProcesses] = useState([])
  const [processes, setProcesses] = useState([])

  const tableSettings = {
    type: TwTable.types.simple
  }

  const workflowTriggers = [
    "trig:webeditor:pre-process",
    "trig:webeditor:pre-post-process",
    "trig:webeditor:post-process"
  ]

  const loadBaseData = async () => {
    await loadAction.executeAsync(async () => {
      const allEnvs = await getInteractEnvironments();

      const hasWfAccess = await getWorkflowAccess();
      setHasWorkflowLicense(hasWfAccess);

      const envGrp = await getEnvironmentGroup(groupId);
      setEnvironmentGroup(envGrp);
      if (hasWfAccess) {
        const wfEnvs = await getWorkflowEnvironments();
        setEnvironments(wfEnvs);

        const tplEnv = await getInteractTemplateEnvironmentNoFiles(environmantId);
        const env = allEnvs.find(({ value }) => value === tplEnv.customerEnvironment);
        setEnvironment({ template: tplEnv, environment: env });

        const file = await getInteractTemplateFile(fileId);
        setLogicFile(file);

        const [wfs, sfs, distLists] = await Promise.all([
          getWorkflows(workflowTriggers.join(",")),
          getSmartforms(),
          getDistributionLists()
        ]);
        setWorkflows(wfs);
        setSmartforms(sfs);
        setDistributionLists(distLists);

        const existingProcesses = await getInteractTemplateFileProcessFromTemplateAndFileId(tplEnv.id, file.id);

        const docs = await getTemplateDocumentsEx(file.id);
        const procs = docs.map(d => {
          const existing = existingProcesses.find(p => p.documentId === d.documentId);
          if (existing) {
            existing["label"] = d.label;
            existing["tmpId"] = uuidv4();
            return existing;
          }
          else
            return { tmpId: uuidv4(), documentId: d.documentId, documentName: d.name, label: d.label, fileId: file.id, templateId: tplEnv.id };
        });

        setProcesses(procs);

        setPaginationTotalCount(procs.length);
        const start = paginationCurrentPage * paginationItemsPerPage;
        setPageProcesses(procs.slice(start, start + paginationItemsPerPage));

      }
      setInitialized(true);
    }, "Failed to load (LogicFileProcessManager)")
  }

  useEffect(() => {
    if (!fileId || !environmantId)
      return;

    loadBaseData();
  }, [environmantId, fileId]);

  useEffect(() => {
    const start = paginationCurrentPage * paginationItemsPerPage;
    setPageProcesses(processes.slice(start, start + paginationItemsPerPage));
  }, [paginationCurrentPage, paginationItemsPerPage]);

  const onRowClick = (item) => {
    // console.log("LogicFileProcessManager", "onRowClick", "item", item);
  }

  const onEditItem = (item, env) => {
    editLogicDocumentProcessesModalHelper.setActivetItem({
      environment: environment,
      item: item,
      logicFile: logicFile,
      environmentGroup: environmentGroup,
      workflows: workflows,
      smartforms: smartforms,
      environments: environments,
      distributionLists: distributionLists,
      pdfAttachmentPolicyOptions: pdfAttachmentPolicyOptions
    });
    editLogicDocumentProcessesModalHelper.open();
  }

  const onRemoveItem = (item, env) => {
    setSelectedProcesses([item]);
    setOpenDeleteConfirmation(true);
  }

  const handleProcessDelete = async () => {
    await deleteAction.executeAsync(async () => {
      await Promise.all(selectedProcesses.map(p => deleteInteractTemplateFileProcess(p.id)));
      await loadBaseData();
      setOpenDeleteConfirmation(false);
      setSelectedProcesses([]);
    }, "Failed to delete Workflows for Logic File Document", "Successfully deleted the Workflows for Logic File Document")
  }

  const getItemActions = (item, env) => {
    var actions = environmentGroup.isVirtual
      ? []
      : [
        {
          name: "Edit",
          onClick: () => onEditItem(item, env)
          // }, {
          //   name: "Remove",
          //   onClick: () => onRemoveItem(item, env),
          //   disabled: !item?.id
        }

      ];
    return actions;
  }

  const isString = (value) => {
    return value => typeof value === 'string' || value instanceof String;
  }

  const createUpdateOrRemoveItem = async (item, values) => {
    if (isString(values.workflowEnvironment))
      values.workflowEnvironment = parseInt(values.workflowEnvironment);
    if (isString(values.pdfAttachmentPolicy))
        values.pdfAttachmentPolicy = parseInt(values.pdfAttachmentPolicy);

    const isEmpty =
      !values.postProcessWorkflowId &&
      !values.prePostProcessWorkflowId &&
      !values.preProcessWorkflowId &&
      !values.smartformId &&
      !values.distributionListId &&
      !values.forceToRunSmartformBeforeEditorSession &&
      !values.textLibraryFolders &&
      !values.isAttachment &&
      (!values.pdfAttachmentPolicy || values.pdfAttachmentPolicy === pdfAttachmentPolicyOptions[0].value);
    // console.log("LogicFileProcessManager", "createOrUpdateItem", "item", item, "values", values, "isEmpty", isEmpty);

    setIsSaving(true);
    let success = false;
    try {
      if (isEmpty) {
        if (!!item.id) {
          await deleteInteractTemplateFileProcess(item.id);
        }
      }
      else {
        if (!!item.id)
          await updateInteractTemplateFileProcess(item.id, values);
        else
          await createInteractTemplateFileProcess(values);
      }
      await loadBaseData();
      success = true;
    }
    catch (err) {
      if (err)
        alert(err);
    }
    finally {
      if (success)
        editLogicDocumentProcessesModalHelper.close();
      setIsSaving(false);
    }
  }

  const getWorkflowName = (wfId) => {
    wfId = wfId || "";
    var wf = workflows.find(w => w.id === wfId);
    return wf?.name || wfId;
  }

  const getWorkflowEnvironmentName = (envId) => {
    var wfe = environments.find(x => x.value === envId);
    return wfe?.name || envId;
  }

  const getSmartformName = (sfId) => {
    sfId = sfId || "";
    var sf = smartforms.find(s => s.id === sfId);
    return sf?.name || sfId;
  }
  const getDistributionListName = (dlId) => {
    dlId = dlId || "";
    var dl = distributionLists.find(d => d.id === dlId);
    return dl?.name || dlId;
  }

  const getDistributionListStatus = (dlId) => {
    dlId = dlId || "";
    var dl = distributionLists.find(d => d.id === dlId);
    return dl?.status || '';
  }

  const getPdfAttachmentPolicy = (policy) => {
    var found = pdfAttachmentPolicyOptions.find(d => d.value === policy);
    return found?.name || '';
  }

  const fullPanelWidth = true;

  return (
    <>
      <TwPage>
        <TwPage.Header>
          <TwPage.Header.Hdr.Fixed
            title={`Manage Workflows for Editor `}
            description={[`Workflow overview for logic file ${logicFile?.fileName || ""}`, "With this feature you can attach workflows for selected documents in the logic file for Editor events"]}
          />
        </TwPage.Header>
        {
          initialized && !hasWorkflowLicense && (
            <>
              <EnvironmentGroupContainerCard
                group={environmentGroup}
                collapsable={false}
              >
                <div className="mt-8 flow-root">
                  <div className={`${fullPanelWidth ? "flex-col tplmtn:flex-row place-content-center" : "flex-col tplmtn:flex-row place-content-center"} flex gap-4 xl:gap-8`}>
                    <CmpPanel additionalClass={`${fullPanelWidth ? "flex-1 mx-12" : "flex-none"}`}>
                      <CmpLabel text={environment?.environment?.name || ""} additionalClass='text-lg font-bold' />


                      <CmpLabel type={CmpLabel.types.warn} additionalClass='mt-8 mb-4'>
                        <p className='text-lg font-bold'>No workflow access/license available</p>
                        <p>The workflow feature requires a access and license for the workflow application</p>
                      </CmpLabel>

                    </CmpPanel>
                  </div>
                </div>
                <div className='h-6'></div>
              </EnvironmentGroupContainerCard>

              <div className='flex mt-8'>
                <div className='flex-1' />
                <CmpButton
                  variant={CmpButton.variants.primary}
                  // className="flex-none w-32 bg-white text-gray-900 hover:bg-gray-100 hover:bg-text-white dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  //disabled={selectedFiles.length === 0}
                  onClick={() => navigate(-1)}
                >
                  Back
                </CmpButton>
              </div>
            </>

          )
        }
        {initialized && hasWorkflowLicense && (
          <>
            <EnvironmentGroupContainerCard
              group={environmentGroup}
              collapsable={false}
            >
              <div className="mt-8 flow-root">
                <div className={`${fullPanelWidth ? "flex-col tplmtn:flex-row place-content-center" : "flex-col tplmtn:flex-row place-content-center"} flex gap-4 xl:gap-8`}>
                  <CmpPanel additionalClass={`${fullPanelWidth ? "flex-1 mx-12" : "flex-none"}`}>
                    <CmpLabel text={environment?.environment?.name || ""} additionalClass='text-lg font-bold' />

                    {hasWorkflowLicense && (
                      <TwTableContext.Provider value={tableSettings}>
                        <div ref={refMenuContainerTo}>
                          <TwTable>
                            <TwTable.Head>
                              <TwTable.Header additionalClass={'sm:pl-6 h-14 uppercase'}>
                                Name
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                Editor On Load
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                QA Before Completion
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                Editor On Completed
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                Smartform Preloader
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                Distribution
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                Attachment
                              </TwTable.Header>
                              <TwTable.Header additionalClass={'sm:pl-6 uppercase'}>
                                Pdf Attachment
                              </TwTable.Header>
                              <TwTable.Header additionalClass='w-2' />
                            </TwTable.Head>
                            <TwTable.Body noDivider={true}>
                              {!!pageProcesses && pageProcesses.map((item, idx) => (
                                <TwTable.BodyRow key={`ff${item.tmpId}-m`} usePointerCursonOnHover={false} onClick={() => onRowClick(item)} additionalClass={classNames(
                                  "",
                                  "h-12 hover:bg-gray-100 cursor-default"
                                )}
                                >
                                  <TwTable.BodyCol>
                                    {item.documentName}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    {getWorkflowName(item.preProcessWorkflowId)}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    {getWorkflowName(item.prePostProcessWorkflowId)}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    {getWorkflowName(item.postProcessWorkflowId)}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    {getSmartformName(item.smartformId)} {item.forceToRunSmartformBeforeEditorSession ? '(Forced)' : ''}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    {getDistributionListName(item.distributionListId)} {getDistributionListStatus(item.distributionListId) === DistributionListsStatus.disabled ? '(disabled)' : ''}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    <IsAttachmentBadge status={item.isAttachment} />
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol>
                                    {getPdfAttachmentPolicy(item.pdfAttachmentPolicy)}
                                  </TwTable.BodyCol>
                                  <TwTable.BodyCol additionalClass='w-2'>
                                    <MenuContextList
                                      actions={getItemActions(item, environment)}
                                      handleDelete="false"
                                      refContainer={refMenuContainerTo}
                                    />
                                  </TwTable.BodyCol>
                                </TwTable.BodyRow>
                              ))}
                            </TwTable.Body>
                            {paginationTotalCount > paginationItemsPerPage && (
                              <TwTable.Foot>
                                <TwTable.FootCol colSpan="20">
                                  <TwTable.Pagination
                                    numPages={Math.ceil(paginationTotalCount / paginationItemsPerPage)}
                                    currPage={paginationCurrentPage}
                                    setCurrPage={setPaginationCurrentPage}
                                    itemsPerPage={paginationItemsPerPage}
                                    setItemPerPage={setPaginationItemsPerPage}
                                    maxItems={paginationTotalCount}
                                    pageRanges={[5, 10, 25, 50]}
                                    boundaryCount={2}
                                  />
                                </TwTable.FootCol>
                              </TwTable.Foot>
                            )}
                          </TwTable>
                        </div>
                      </TwTableContext.Provider>
                    )}
                  </CmpPanel>
                </div>
              </div>
              <div className='h-6'></div>
            </EnvironmentGroupContainerCard>

            <div className='flex mt-8'>
              <div className='flex-1' />
              <CmpButton
                variant={CmpButton.variants.primary}
                // className="flex-none w-32 bg-white text-gray-900 hover:bg-gray-100 hover:bg-text-white dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                //disabled={selectedFiles.length === 0}
                onClick={() => navigate(-1)}
              >
                Back
              </CmpButton>
            </div>
          </>
        )}
      </TwPage>

      <EditLogicDocumentProcessesModal
        title={`Edit Workflows for document`}
        modalHelper={editLogicDocumentProcessesModalHelper}
        onConfirm={createUpdateOrRemoveItem}
        isSaving={isSaving}
        workflowTriggers={workflowTriggers}
      />

      <GenericModal open={openDeleteConfirmation} setOpen={setOpenDeleteConfirmation}
        onConfirm={handleProcessDelete}
        onCancel={() => {
          setOpenDeleteConfirmation(false);
          setSelectedProcesses([]);
        }}
        showCancelButton={true}
        confirmButtonText={'Delete'}
        title="Delete Workflows for Logic File Document">
        <div className="mt-4">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete the selected Workflows for Logic File Document?
          </p>
        </div>
      </GenericModal>
    </>
  );
}

const IsAttachmentBadge = ({ status }) => {
  const isAttachmentStatusNames = {
    [true]: 'Yes',
    [false]: 'No',
  };

  const getBadgeType = (status) => {
    switch (status) {
      case true:
        return badgeTypes.success;
      case false:
      default:
        return badgeTypes.warn;
    }
  };

  if (status === null || status === undefined)
    return <></>;
  return <Badge type={getBadgeType(status)} text={isAttachmentStatusNames[status]} />;
};